import { useState } from "react";
import { MapPin, ArrowUpRight } from "lucide-react";
import { properties } from "../../lib/properties";
import { site } from "../../lib/siteConfig";

const typeStyles = {
  transitorio: "bg-brand-ink text-white",
  breve: "bg-white text-brand-ink border border-brand-ink/20",
  "4+4": "bg-brand-beige text-brand-ink",
  "3+2": "bg-brand-beige text-brand-ink",
  vendita: "bg-brand-success text-white",
};

const statusStyles = {
  Locato: "bg-brand-success text-white",
  "In conversione": "bg-brand-beige text-brand-ink",
  "In vendita": "bg-brand-ink text-white",
  "In partenza": "bg-brand-beige text-brand-ink",
  "In arrivo": "bg-brand-beigeDark text-brand-ink",
  Convertito: "bg-brand-success text-white",
  Venduto: "bg-brand-ink text-white",
};

const ComingSoonPlaceholder = ({ p }) => (
  <div
    data-testid={`property-placeholder-${p.id}`}
    className="absolute inset-0 w-full h-full flex items-center justify-center bg-brand-ink overflow-hidden"
  >
    {/* FG watermark */}
    <div
      aria-hidden
      className="absolute font-display italic font-black leading-none select-none pointer-events-none"
      style={{
        fontSize: "320px",
        color: "rgba(228, 220, 207, 0.06)",
        right: "-40px",
        bottom: "-60px",
      }}
    >
      FG
    </div>
    <div className="relative text-center px-6">
      <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-brand-beige">
        <span className="inline-block align-middle h-px bg-brand-beige/70 w-8 mr-2" />
        Prossimamente
      </div>
      <div className="mt-3 font-display text-3xl sm:text-4xl font-black text-white leading-tight">
        In <span className="italic font-normal">arrivo</span>
      </div>
      <div className="mt-2 text-xs text-brand-beige/80 uppercase tracking-[0.22em]">
        {p.address}
      </div>
    </div>
  </div>
);

const PropertyCard = ({ p }) => {
  const [active, setActive] = useState(0);
  const isComingSoon = p.comingSoon;
  return (
    <article
      data-testid={`property-card-${p.id}`}
      className={`group bg-white border border-brand-line rounded-sm overflow-hidden flex flex-col transition-all duration-300 ${
        isComingSoon ? "opacity-60 hover:opacity-90" : ""
      }`}
    >
      {/* Main image */}
      <div className="relative aspect-[4/3] overflow-hidden bg-brand-beige/30">
        {isComingSoon ? (
          <ComingSoonPlaceholder p={p} />
        ) : (
          p.images.map((src, i) => (
            <img
              key={src}
              src={src}
              alt={`${p.address} · foto ${i + 1}`}
              loading="lazy"
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                i === active ? "opacity-100" : "opacity-0"
              }`}
            />
          ))
        )}
        <div className="absolute top-4 left-4 flex items-center gap-2 z-10">
          <span
            data-testid={`property-type-${p.id}`}
            className={`inline-flex items-center text-[11px] uppercase tracking-[0.18em] font-semibold px-3 py-1.5 rounded-sm ${
              typeStyles[p.typeKey] || typeStyles.transitorio
            }`}
          >
            {p.type}
          </span>
          {p.status && (
            <span
              className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-semibold px-3 py-1.5 rounded-sm ${
                statusStyles[p.status] || "bg-brand-success text-white"
              }`}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
              {p.status}
            </span>
          )}
        </div>
      </div>

      {/* Thumbnails */}
      {!isComingSoon && p.images.length > 1 && (
        <div className="flex gap-1.5 p-1.5 bg-brand-bg border-b border-brand-line">
          {p.images.map((src, i) => (
            <button
              key={src}
              type="button"
              onClick={() => setActive(i)}
              data-testid={`property-thumb-${p.id}-${i}`}
              aria-label={`Foto ${i + 1}`}
              className={`flex-1 aspect-[4/3] overflow-hidden rounded-sm transition-all ${
                i === active
                  ? "ring-2 ring-brand-ink opacity-100"
                  : "opacity-60 hover:opacity-90"
              }`}
            >
              <img
                src={src}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      {/* Info */}
      <div className="p-6 sm:p-7 flex flex-col flex-1">
        <div className="flex items-start gap-2 text-brand-muted text-xs uppercase tracking-[0.18em] font-semibold">
          <MapPin size={13} className="mt-0.5 shrink-0" />
          <span>{p.area}</span>
        </div>
        <h3 className="mt-3 font-display text-xl sm:text-2xl text-brand-ink leading-tight">
          {p.address}
        </h3>

        <dl className="mt-5 grid grid-cols-2 gap-y-3 gap-x-4 text-sm">
          {p.duration && (
            <div>
              <dt className="text-[11px] uppercase tracking-[0.18em] text-brand-muted">Durata</dt>
              <dd className="mt-1 text-brand-text font-medium">
                {p.duration}
                {p.size ? <span className="text-brand-muted"> · {p.size}</span> : null}
              </dd>
            </div>
          )}
          {!p.duration && p.size && (
            <div>
              <dt className="text-[11px] uppercase tracking-[0.18em] text-brand-muted">Metratura</dt>
              <dd className="mt-1 text-brand-text font-medium">{p.size}</dd>
            </div>
          )}
          {p.price && (
            <div>
              <dt className="text-[11px] uppercase tracking-[0.18em] text-brand-muted">
                {p.priceLabel || "Canone"}
              </dt>
              <dd className="mt-1 text-brand-ink font-display text-2xl leading-none">
                {p.price}
                <span className="text-sm text-brand-muted font-sans ml-1 align-middle">
                  {p.priceSuffix}
                </span>
              </dd>
            </div>
          )}
        </dl>

        {p.note && (
          <p className="mt-4 text-sm italic leading-relaxed text-brand-muted border-l-2 border-brand-beigeDark pl-4">
            {p.note}
          </p>
        )}

        <div className="mt-6 pt-5 border-t border-brand-line">
          <a
            href={site.whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`property-cta-${p.id}`}
            className="inline-flex items-center gap-2 text-sm font-medium text-brand-ink hover:text-brand-inkHover transition-colors"
          >
            Voglio un risultato così
            <ArrowUpRight size={15} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>
      </div>
    </article>
  );
};

export const Portfolio = () => {
  if (!properties.length) return null;
  const single = properties.length === 1;

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="relative bg-brand-bg py-24 sm:py-32 lg:py-40"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14 sm:mb-16">
          <div className="max-w-2xl">
            <p className="divider-ornament" data-testid="portfolio-eyebrow">
              Alcuni immobili che gestisco
            </p>
            <h2
              data-testid="portfolio-headline"
              className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.05] font-bold text-brand-ink mt-8"
            >
              Case vere, <span className="italic font-normal">rendite vere</span>.
            </h2>
          </div>
          <p className="max-w-md text-base sm:text-lg leading-relaxed text-brand-muted">
            Una selezione di immobili seguiti: tipologia di contratto, durata e canone mensile.
            Nessun numero di fantasia — solo locazioni e vendite chiuse davvero.
          </p>
        </div>

        <div
          className={`grid gap-6 lg:gap-8 ${
            single
              ? "md:grid-cols-1 lg:grid-cols-2 lg:max-w-4xl"
              : "md:grid-cols-2 lg:grid-cols-3"
          }`}
        >
          {properties.map((p) => (
            <PropertyCard key={p.id} p={p} />
          ))}
        </div>
      </div>
    </section>
  );
};
